/**
 * gstr1Json.ts — Transform a GstReturnDataset into the GST portal's GSTR-1
 * JSON schema (as consumed by the GSTN offline tool / Returns Offline Utility).
 *
 * Sections implemented: b2b, b2cs, cdnr, cdnur, hsn, doc_issue. These cover the
 * outward supplies + credit notes a POS/retail filer needs. Exports (exp) are
 * out of scope for v1.
 *
 * Reference field names follow the GSTR-1 e-file schema:
 *   fp   filing period MMYYYY
 *   gstin supplier GSTIN
 *   b2b[].ctin / inv[].inum,idt,val,pos,rchrg,inv_typ,itms[].itm_det{rt,txval,iamt,camt,samt,csamt}
 *   b2cs[]{sply_ty,typ,pos,rt,txval,iamt,camt,samt,csamt}
 *   hsn.data[]{num,hsn_sc,desc,uqc,qty,txval,iamt,camt,samt,csamt,rt}
 *   doc_issue.doc_det[].docs[]{num,from,to,totnum,cancel,net_issue}
 */
import { round2 } from './gstReturns';
function filingPeriod(month, year) {
    return `${String(month + 1).padStart(2, '0')}${year}`;
}
function fmtDate(d) {
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}-${mm}-${d.getFullYear()}`;
}
/** Build the GSTR-1 JSON object (ready to JSON.stringify). */
export function buildGstr1Json(ds, opts) {
    var _a, _b;
    const out = {
        gstin: opts.gstin,
        fp: filingPeriod(opts.month, opts.year),
        gt: round2((_a = opts.grossTurnover) !== null && _a !== void 0 ? _a : 0),
        // Current-period turnover includes exempt/nil supplies, not just taxable.
        cur_gt: round2(ds.totals.taxableValue + ds.gstr3b.nilExemptSupplies),
    };
    // ── B2B ──
    if (ds.b2b.length > 0) {
        out.b2b = ds.b2b.map((party) => ({
            ctin: party.ctin,
            inv: party.invoices.map((iv) => ({
                inum: iv.invoiceNumber,
                idt: fmtDate(iv.date),
                val: round2(iv.invoiceValue),
                pos: iv.posCode,
                rchrg: iv.reverseCharge ? 'Y' : 'N',
                inv_typ: 'R',
                itms: iv.items.map((it, idx) => ({
                    num: idx + 1,
                    itm_det: {
                        rt: it.rate,
                        txval: round2(it.taxableValue),
                        iamt: round2(it.igst),
                        camt: round2(it.cgst),
                        samt: round2(it.sgst),
                        csamt: round2(it.cess),
                    },
                })),
            })),
        }));
    }
    // ── B2CL (Table 5): large inter-state B2C sales, invoice-wise per POS ──
    if (ds.b2cl.length > 0) {
        out.b2cl = ds.b2cl.map((p) => ({
            pos: p.posCode,
            inv: p.invoices.map((iv) => ({
                inum: iv.invoiceNumber,
                idt: fmtDate(iv.date),
                val: round2(iv.invoiceValue),
                itms: iv.items.map((it, idx) => ({
                    num: idx + 1,
                    itm_det: {
                        rt: it.rate,
                        txval: round2(it.taxableValue),
                        iamt: round2(it.igst),
                        csamt: round2(it.cess),
                    },
                })),
            })),
        }));
    }
    // ── B2CS ──
    if (ds.b2cs.length > 0) {
        out.b2cs = ds.b2cs.map((r) => ({
            sply_ty: r.supplyType,
            typ: 'OE',
            pos: r.posCode,
            rt: r.rate,
            txval: round2(r.taxableValue),
            iamt: round2(r.igst),
            camt: round2(r.cgst),
            samt: round2(r.sgst),
            csamt: round2(r.cess),
        }));
    }
    // ── Nil / exempt / non-GST (Table 8) ──
    if (ds.nilExempt.length > 0) {
        const SPLY_TY = {
            INTER_REG: 'INTRB2B', INTER_UNREG: 'INTRB2C',
            INTRA_REG: 'INTRAB2B', INTRA_UNREG: 'INTRAB2C',
        };
        out.nil = {
            inv: ds.nilExempt.map((r) => ({
                sply_ty: SPLY_TY[r.category],
                nil_amt: round2(r.nilRated),
                expt_amt: round2(r.exempt),
                ngsup_amt: round2(r.nonGst),
            })),
        };
    }
    // ── CDNR (credit/debit notes to registered) ──
    if (ds.cdnr.length > 0) {
        out.cdnr = ds.cdnr.map((party) => ({
            ctin: party.ctin,
            nt: party.notes.map((n) => ({
                ntty: n.noteType,
                nt_num: n.noteNumber,
                nt_dt: fmtDate(n.date),
                val: round2(n.value),
                pos: n.posCode,
                rchrg: n.reverseCharge ? 'Y' : 'N',
                inv_typ: 'R',
                itms: n.items.map((it, idx) => ({
                    num: idx + 1,
                    itm_det: {
                        rt: it.rate,
                        txval: round2(it.taxableValue),
                        iamt: round2(it.igst),
                        camt: round2(it.cgst),
                        samt: round2(it.sgst),
                        csamt: round2(it.cess),
                    },
                })),
            })),
        }));
    }
    // ── CDNUR (credit/debit notes to unregistered, B2CL) ──
    if (ds.cdnur.length > 0) {
        out.cdnur = ds.cdnur.map((n) => ({
            typ: n.supplyType,
            ntty: n.noteType,
            nt_num: n.noteNumber,
            nt_dt: fmtDate(n.date),
            val: round2(n.value),
            pos: n.posCode,
            itms: n.items.map((it, idx) => ({
                num: idx + 1,
                itm_det: {
                    rt: it.rate,
                    txval: round2(it.taxableValue),
                    iamt: round2(it.igst),
                    camt: round2(it.cgst),
                    samt: round2(it.sgst),
                    csamt: round2(it.cess),
                },
            })),
        }));
    }
    // ── HSN ──
    if (ds.hsn.length > 0) {
        out.hsn = {
            data: ds.hsn.map((h, idx) => ({
                num: idx + 1,
                hsn_sc: h.hsn,
                desc: h.description || h.hsn,
                uqc: h.uqc,
                qty: round2(h.quantity),
                txval: round2(h.taxableValue),
                iamt: round2(h.igst),
                camt: round2(h.cgst),
                samt: round2(h.sgst),
                csamt: round2(h.cess),
                rt: h.rate,
            })),
        };
    }
    // ── Documents issued (Table 13) ──
    // Nature-of-document codes: 1 = Invoices for outward supply, 5 = Credit note.
    // Each series (per prefix + device slot) is a separate `docs` row.
    if (ds.docIssued.length > 0) {
        const DOC_NUM = { INVOICE: 1, CREDIT_NOTE: 5 };
        const byType = new Map();
        for (const d of ds.docIssued) {
            const docNum = DOC_NUM[d.docType];
            const arr = (_b = byType.get(docNum)) !== null && _b !== void 0 ? _b : [];
            arr.push(d);
            byType.set(docNum, arr);
        }
        out.doc_issue = {
            doc_det: [...byType.entries()]
                .sort(([a], [b]) => a - b)
                .map(([docNum, ranges]) => ({
                doc_num: docNum,
                docs: ranges.map((d, idx) => ({
                    num: idx + 1,
                    from: d.from,
                    to: d.to,
                    totnum: d.totalNumber,
                    cancel: d.cancelled,
                    net_issue: d.totalNumber - d.cancelled,
                })),
            })),
        };
    }
    return out;
}
/** Convenience: pretty-printed JSON string ready for `file.saveText`. */
export function stringifyGstr1(ds, opts) {
    return JSON.stringify(buildGstr1Json(ds, opts), null, 2);
}
